import { Injectable } from '@angular/core';
import {
  DEFAULT_DURATION_IN_MINUTES,
  LOCALIZATION_LOCALE,
  STORAGE_KEY,
  CachedCalendarData,
} from '../../models';
import { EncryptionUtils } from '../../utils';
import { SerializeUtils } from '../../utils/serialize.utils';

@Injectable()
export abstract class AbstractStorageService {
  private static passphrase: string;

  public async init(passphrase: string) {
    AbstractStorageService.passphrase = passphrase;
  }

  //region LANGUAGE
  public saveSelectedLanguage(language: LOCALIZATION_LOCALE): boolean {
    return this.saveData(STORAGE_KEY.LANGUAGE, language);
  }

  public getSelectedLanguage(): LOCALIZATION_LOCALE {
    return this.getAndParseData(STORAGE_KEY.LANGUAGE);
  }
  //endregion

  //region LAST_SELECTED_COUNTRY_CODE
  public saveLastSelectedCountryCode(countryCode: string): boolean {
    return this.saveData(STORAGE_KEY.LAST_SELECTED_COUNTRY_CODE, countryCode);
  }

  public getLastSelectedCountryCode(): string {
    return this.getAndParseData(STORAGE_KEY.LAST_SELECTED_COUNTRY_CODE);
  }
  //endregion

  //region AVAILABLE SLOT VIEW MODE
  public saveSelectedAvailableSlotViewMode(mode: string): boolean {
    return this.saveData(STORAGE_KEY.AVAILABLE_SLOT_VIEW_MODE, mode);
  }

  public getSelectedAvailableSlotViewMode(): string {
    return this.getAndParseData(STORAGE_KEY.AVAILABLE_SLOT_VIEW_MODE);
  }
  //endregion

  //region LAST_SELECTED_DURATION
  public saveLastSelectedDuration(duration: number): boolean {
    // if duration > DEFAULT_DURATION_IN_MINUTES -> it can be that a whole day blocker was placed
    if (!duration || duration < 0 || duration > DEFAULT_DURATION_IN_MINUTES) {
      return false;
    }
    return this.saveData(STORAGE_KEY.LAST_SELECTED_DURATION, duration);
  }

  public getLastSelectedDuration(): number {
    return this.getAndParseData(STORAGE_KEY.LAST_SELECTED_DURATION);
  }
  //endregion

  //region AUTH TOKEN

  public saveAuthToken(authToken: string): boolean {
    return this.saveData(STORAGE_KEY.AUTH_TOKEN, authToken);
  }

  public getAuthToken(): string {
    return this.getAndParseData(STORAGE_KEY.AUTH_TOKEN);
  }

  public removeAuthToken(): boolean {
    return this.removeData(STORAGE_KEY.AUTH_TOKEN);
  }

  //endregion

  //region RESEND PIN COUNTDOWN
  public saveResendPinTimestamp(timestamp: Date): boolean {
    return this.saveData(STORAGE_KEY.RESEND_PIN_TIMESTAMP, timestamp.getTime());
  }

  public getResendPinLoginTimestamp(): Date {
    const savedTimestamp = this.getAndParseData(
      STORAGE_KEY.RESEND_PIN_TIMESTAMP
    );
    if (savedTimestamp) {
      return new Date(savedTimestamp);
    } else {
      return null;
    }
  }

  public removeResendPinTimestamp(): boolean {
    return this.removeData(STORAGE_KEY.RESEND_PIN_TIMESTAMP);
  }
  //endregion

  // region EMAIL
  public async saveEmail(email: string) {
    const encryptedEmail = await EncryptionUtils.encryptString(
      email,
      AbstractStorageService.passphrase
    );
    this.saveData(STORAGE_KEY.EMAIL, encryptedEmail);
  }

  public async getEmail(): Promise<string> {
    const encryptedEmail = this.getAndParseData(STORAGE_KEY.EMAIL);
    const decryptedEmail = await EncryptionUtils.decryptToString(
      encryptedEmail,
      AbstractStorageService.passphrase
    );
    return decryptedEmail;
  }
  // endregion

  // region CACHED_CALENDAR_DATA
  public async saveCachedCalendarData(data: Map<number, CachedCalendarData>) {
    const encryptedData = await EncryptionUtils.encryptObject(
      data,
      AbstractStorageService.passphrase
    );

    this.saveData(STORAGE_KEY.CACHED_CALENDAR_DATA, encryptedData);
  }

  public async getCachedCalendarData(): Promise<
    Map<number, CachedCalendarData>
  > {
    const encryptedData = this.getAndParseData(
      STORAGE_KEY.CACHED_CALENDAR_DATA
    );
    const decryptedData: Map<number, CachedCalendarData> =
      await EncryptionUtils.decryptToObject(
        encryptedData,
        AbstractStorageService.passphrase
      );
    return decryptedData;
  }

  public removeCachedCalendarData() {
    this.removeData(STORAGE_KEY.CACHED_CALENDAR_DATA);
  }
  // endregion

  public getAndParseData(key: string): any {
    const item = localStorage.getItem(key);
    if (item) {
      try {
        return JSON.parse(item, SerializeUtils.reviver);
      } catch (e) {
        console.error(e.toString());
        return item;
      }
    } else {
      return null;
    }
  }

  public saveData(key: string, data: any): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(data, SerializeUtils.replacer));
      return true;
    } catch (e) {
      return false;
    }
  }

  public removeData(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }
}
